// src/components/Calculator/StepEditorModal.tsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { X, AlertCircle, ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import { useCalculatorStore } from '@/store/calculator';
import { VariableType } from '@/lib/calculator';
import { cn } from '@/lib/utils';
import ReactDOM from 'react-dom';

interface StepEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  variableType: VariableType | null;
}

// Define the direction options for stepping
type StepDirection = 'both' | 'up' | 'down';

// Use the same scroll locking implementation as other modals
const useScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    if (!isLocked) return;
    
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    
    // Save current scroll position
    const scrollY = window.scrollY;
    
    // Apply scroll lock
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore original styles and scroll position
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = originalOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isLocked]);
};

// Constants for time conversions
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const AVG_DAYS_PER_MONTH = 30.436875; // Average days per month (365.2425/12)
const MS_PER_MONTH = AVG_DAYS_PER_MONTH * MS_PER_DAY;

export function StepEditorModal({ isOpen, onClose, variableType }: StepEditorModalProps) {
  const { values, updateStep, updateStepDirection } = useCalculatorStore();
  const [stepValue, setStepValue] = useState<string>('');
  const [stepDirection, setStepDirection] = useState<StepDirection>('both');
  const [localValue, setLocalValue] = useState<number>(0);
  const [isFocused, setIsFocused] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Apply scroll lock
  useScrollLock(isOpen);

  // Validation helper functions (similar to VariableInput)
  const getStepWarningMessage = useCallback((type: VariableType, value: number): string | null => {
    if (type === 'interest' && (value < 0.1 || value > 5)) {
      return 'Interest rate step should be between 0.1% and 5%';
    }
    if (type === 'price' && (value < 1000 || value > 500000)) {
      return 'Price step should be between $1,000 and $500,000';
    }
    if (type === 'income' && (value < 1000 || value > 100000)) {
      return 'Income step should be between $1,000 and $100,000';
    }
    if (type === 'downPayment' && (value < 1000 || value > 100000)) {
      return 'Down payment step should be between $1,000 and $100,000';
    }
    if (type === 'savings' && (value < 50 || value > 10000)) {
      return 'Monthly savings step should be between $50 and $10,000';
    }
    if (type === 'date' && (value < 1 || value > 60)) {
      return 'Date step should be between 1 and 60 months';
    }
    return null;
  }, []);

  const parseStepValue = useCallback((type: VariableType, inputValue: string): number => {
    if (!inputValue || inputValue.trim() === '') {
      return 0;
    }
    
    try {
      switch (type) {
        case 'price':
        case 'income':
        case 'downPayment':
        case 'savings':
        case 'date': {
          const cleanValue = inputValue.replace(/[^0-9.-]/g, '');
          if (!cleanValue) {
            setErrorMessage('');
            return 0;
          }
          
          const parsed = parseInt(cleanValue, 10);
          if (isNaN(parsed) || !isFinite(parsed)) {
            setErrorMessage('Please enter a valid number');
            return 0;
          }
          
          setErrorMessage('');
          return parsed;
        }
        case 'interest': {
          if (!inputValue.trim()) {
            setErrorMessage('');
            return 0;
          }
          
          const parsed = parseFloat(inputValue);
          if (isNaN(parsed) || !isFinite(parsed)) {
            setErrorMessage('Please enter a valid interest rate');
            return 0;
          }
          setErrorMessage('');
          return parsed;
        }
        default:
          return 0;
      }
    } catch (error) {
      setErrorMessage('An error occurred while processing the input');
      return 0;
    }
  }, []);

  const enforceStepMinMax = useCallback((type: VariableType, val: number): number => {
    if (val === null || val === undefined || isNaN(val)) {
      return getDefaultStepNumber(type);
    }
    
    try {
      switch (type) {
        case 'interest':
          return Math.max(0.1, Math.min(5, val));
        case 'price':
          return Math.max(1000, Math.min(500000, val));
        case 'income':
          return Math.max(1000, Math.min(100000, val));
        case 'downPayment':
          return Math.max(1000, Math.min(100000, val));
        case 'savings':
          return Math.max(50, Math.min(10000, val));
        case 'date':
          return Math.max(1, Math.min(60, val));
        default:
          return Math.max(1, val);
      }
    } catch (error) {
      return getDefaultStepNumber(type);
    }
  }, []);

  const formatStepValue = useCallback((type: VariableType, val: number): string => {
    if (val === null || val === undefined || isNaN(val)) {
      return type === 'interest' ? '0.00' : '0';
    }
    
    try {
      switch (type) {
        case 'price':
        case 'income':
        case 'downPayment':
        case 'savings':
        case 'date':
          // Always format with commas for currency/number fields (like VariableInput)
          return new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: 0,
          }).format(val);
        case 'interest':
          return isFocused ? val.toString() : val.toFixed(2);
        default:
          return val.toString();
      }
    } catch (error) {
      return '0';
    }
  }, [isFocused]);

  const getDefaultStepNumber = (type: VariableType): number => {
    switch (type) {
      case 'price': return 50000;
      case 'income': return 10000;
      case 'downPayment': return 10000;
      case 'savings': return 500;
      case 'interest': return 0.5;
      case 'date': return 12;
      default: return 1;
    }
  };

  // Reset the step value and direction when the modal opens or the variable changes
  useEffect(() => {
    if (isOpen && variableType) {
      const currentStep = values[variableType].step;
      const currentDirection = values[variableType].stepDirection || 'both';
      
      // Display months for date type instead of milliseconds
      let displayValue: number;
      if (variableType === 'date') {
        displayValue = Math.round(currentStep / MS_PER_MONTH);
      } else {
        displayValue = currentStep;
      }
      
      setLocalValue(displayValue);
      setStepDirection(currentDirection as StepDirection);
      setErrorMessage('');
      setShowWarning(false);
    }
  }, [isOpen, variableType, values]);

  // Handle closing the modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle input value change with real-time validation
  const handleValueChange = useCallback((newInputValue: string) => {
    if (!variableType) return;

    try {
      // Handle empty input
      if (!newInputValue || newInputValue.trim() === '') {
        setStepValue('');
        setLocalValue(0);
        setShowWarning(true);
        return;
      }

      // Handle formatting for currency fields
      if (['price', 'income', 'downPayment', 'savings'].includes(variableType)) {
        const rawValue = newInputValue.replace(/[^\d,]/g, '');
        const numericValue = rawValue.replace(/,/g, '');
        if (numericValue === '') {
          setStepValue('');
          setLocalValue(0);
        } else {
          const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          setStepValue(formattedValue);
          const parsed = parseInt(numericValue, 10);
          if (!isNaN(parsed)) {
            setLocalValue(parsed);
            const warningMsg = getStepWarningMessage(variableType, parsed);
            setShowWarning(!!warningMsg);
          }
        }
      } else if (variableType === 'interest') {
        // Allow decimal input for interest
        if (/^\d*\.?\d*$/.test(newInputValue)) {
          setStepValue(newInputValue);
          const parsed = parseFloat(newInputValue);
          if (!isNaN(parsed)) {
            setLocalValue(parsed);
            const warningMsg = getStepWarningMessage(variableType, parsed);
            setShowWarning(!!warningMsg);
          }
        }
      } else {
        // Handle other types (date)
        const newValue = parseStepValue(variableType, newInputValue);
        setStepValue(newInputValue);
        setLocalValue(newValue);
        const warningMsg = getStepWarningMessage(variableType, newValue);
        setShowWarning(!!warningMsg);
      }
    } catch (error) {
      setErrorMessage('An error occurred while processing your input');
    }
  }, [variableType, parseStepValue, getStepWarningMessage]);

  const handleFocus = useCallback(() => {
    if (!variableType) return;
    setIsFocused(true);
    // Always use the current localValue when focusing (corrected value)
    if (variableType === 'interest') {
      setStepValue(localValue.toString());
    }
    setErrorMessage('');
  }, [variableType, localValue]);

  const handleBlur = useCallback(() => {
    if (!variableType) return;
    setIsFocused(false);
    
    try {
      // Enforce minimum values and auto-correct
      const correctedValue = enforceStepMinMax(variableType, localValue);
      if (correctedValue !== localValue) {
        setLocalValue(correctedValue);
      }
      setShowWarning(false);
    } catch (error) {
      setErrorMessage('An error occurred while validating the input');
    }
  }, [variableType, localValue, enforceStepMinMax]);

  // Only render when open and we have a variable type
  if (!isOpen || !variableType) return null;

  const handleSave = () => {
    if (!variableType) return;
    
    // Use the auto-corrected local value
    const finalValue = enforceStepMinMax(variableType, localValue);
    
    // Convert months to milliseconds for date type
    let storeValue = finalValue;
    if (variableType === 'date') {
      storeValue = finalValue * MS_PER_MONTH;
    }
    
    // Update the step and step direction in the store
    updateStep(variableType, storeValue);
    updateStepDirection(variableType, stepDirection);
    onClose();
  };

  const getLabel = () => {
    switch (variableType) {
      case 'price': return 'Price Step ($)';
      case 'income': return 'Income Step ($)';
      case 'downPayment': return 'Down Payment Step ($)';
      case 'savings': return 'Monthly Savings Step ($)';
      case 'interest': return 'Interest Rate Step (%)';
      case 'date': return 'Date Step (months)';
      default: return 'Step Value';
    }
  };

  const getDefaultDirection = (): StepDirection => {
    switch (variableType) {
      case 'income':
      case 'downPayment':
      case 'savings':
      case 'date':
        return 'up';
      default:
        return 'both';
    }
  };

  const handleReset = () => {
    const defaultValue = getDefaultStepNumber(variableType);
    setLocalValue(defaultValue);
    setStepDirection(getDefaultDirection());
    setErrorMessage('');
    setShowWarning(false);
  };

  const getDescription = () => {
    switch (variableType) {
      case 'price':
        return 'This controls how much the home price changes between grid cells. Default is $50,000.';
      case 'income':
        return 'This controls how much the annual income changes between grid cells. Default is $10,000.';
      case 'downPayment':
        return 'This controls how much the down payment changes between grid cells. Default is $10,000.';
      case 'savings':
        return 'This controls how much the monthly savings changes between grid cells. Default is $500.';
      case 'interest':
        return 'This controls how much the interest rate changes between grid cells. Default is 0.5%.';
      case 'date':
        return 'This controls how many months the date changes between grid cells. Default is 12 months (1 year).';
      default:
        return 'This controls how much the value changes between grid cells.';
    }
  };

  const warningMsg = getStepWarningMessage(variableType, localValue);

  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div 
        ref={modalRef}
        className="fixed z-[100] bg-white rounded-lg shadow-xl w-full max-w-md"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Edit Variable: {values[variableType].label}
            </h2>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200"
              onClick={onClose}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-4">
            {getDescription()}
          </p>

          {/* Input Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {getLabel()}
            </label>
            <input
              type="text"
              value={isFocused && variableType === 'interest' ? stepValue : formatStepValue(variableType, localValue)}
              onChange={(e) => handleValueChange(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={cn(
                "w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary",
                errorMessage && "border-red-500",
                showWarning && !errorMessage && "border-amber-400"
              )}
            />
            
            {/* Warning display */}
            {showWarning && warningMsg && !errorMessage && (
              <div className="flex items-center gap-2 mt-2 text-xs text-amber-600">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{warningMsg}</span>
              </div>
            )}
            
            {/* Error display */}
            {errorMessage && (
              <div className="flex items-center gap-2 mt-2 text-xs text-red-500">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
          
          {/* Step Direction Control */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Step Direction
            </label>
            <div className="flex">
              <button
                onClick={() => setStepDirection('both')}
                className={cn(
                  "flex-1 flex justify-center items-center gap-1 py-2 border border-gray-300 rounded-l",
                  stepDirection === 'both' ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                <ArrowUpDown className="w-4 h-4" />
                <span>Both</span>
              </button>
              <button
                onClick={() => setStepDirection('up')}
                className={cn(
                  "flex-1 flex justify-center items-center gap-1 py-2 border-t border-b border-r border-gray-300",
                  stepDirection === 'up' ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                <ChevronUp className="w-4 h-4" />
                <span>Up 2x</span>
              </button>
              <button
                onClick={() => setStepDirection('down')}
                className={cn(
                  "flex-1 flex justify-center items-center gap-1 py-2 border-t border-b border-r border-gray-300 rounded-r",
                  stepDirection === 'down' ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                <ChevronDown className="w-4 h-4" />
                <span>Down 2x</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {stepDirection === 'both' 
                ? `Show one step up and one step down from base value${variableType === 'price' ? ' (default for price)' : variableType === 'interest' ? ' (default for interest rate)' : ''}`
                : stepDirection === 'up' 
                  ? `Show two steps up from base value${variableType === 'income' ? ' (default for income)' : variableType === 'downPayment' ? ' (default for down payment)' : variableType === 'savings' ? ' (default for monthly savings)' : variableType === 'date' ? ' (default for date)' : ''}` 
                  : "Show two steps down from base value"}
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              Reset to Default
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-white bg-primary rounded hover:bg-primary-dark"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}